import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader, Wand2, ShieldCheck, Search, Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { ModelCanvasCard, CARD_W, cardHeight } from "@/components/bi/ModelCanvasCard";
import { RelationshipDialog, CARDINALITY_OPTIONS } from "@/components/bi/RelationshipDialog";
import { ModelValidationSheet, AutodetectAllDialog } from "@/components/bi/ModelValidation";
import { MeasureEditorDialog, CalcFieldDialog } from "@/components/bi/CalcEditors";

export { CARDINALITY_OPTIONS, RelationshipDialog };

function defaultPositions(datasets) {
  const pos = {};
  datasets.forEach((d, i) => { pos[d.id] = { x: 40 + (i % 3) * (CARD_W + 90), y: 40 + Math.floor(i / 3) * 320 }; });
  return pos;
}

// Which end the filter flows *to* in single direction: the many side (M:M → left).
function arrowTarget(c) {
  if ((c.cross_filter_direction || "single") === "both") return "both";
  const card = c.cardinality || "many_to_one";
  if (card === "one_to_one") return "both";
  if (card === "one_to_many") return "right";
  return "left";
}

// Visual model canvas: draggable dataset cards + relationship lines that drive
// the cross-dataset (model-join) query engine.
export function ModelCanvas({ datasets, onContractsChanged, onDatasetsChanged }) {
  const [contracts, setContracts] = useState(null);
  const [positions, setPositions] = useState({});
  const [models, setModels] = useState({});
  const [dialog, setDialog] = useState(null);   // {contract} | {create:true} | {initial}
  const [validateOpen, setValidateOpen] = useState(false);
  const [autoOpen, setAutoOpen] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [measuresOpen, setMeasuresOpen] = useState(() => new Set());
  const [measDlg, setMeasDlg] = useState(null);  // {ds, model, editMeasure}
  const [calcDlg, setCalcDlg] = useState(null);  // {ds, editField}
  const saveTimer = useRef(null);

  const load = useCallback(() => {
    api.get("/join-contracts").then(({ data }) => setContracts(data)).catch(() => setContracts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/model-canvas").then(({ data }) => {
      setPositions((p) => ({ ...defaultPositions(datasets), ...p, ...(data.positions || {}) }));
    }).catch(() => setPositions(defaultPositions(datasets)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setPositions((p) => ({ ...defaultPositions(datasets), ...p })); }, [datasets]);

  const loadModel = useCallback((dsId) => {
    api.get(`/datasets/${dsId}/model`).then(({ data }) => setModels((m) => ({ ...m, [dsId]: data }))).catch(() => {});
  }, []);

  const persistPositions = (pos) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => api.put("/model-canvas", { positions: pos }).catch(() => {}), 700);
  };

  const startDrag = (dsId) => (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const start = positions[dsId] || { x: 40, y: 40 };
    const sx = e.clientX, sy = e.clientY;
    const onMove = (me) => setPositions((p) => ({ ...p, [dsId]: { x: Math.max(0, start.x + me.clientX - sx), y: Math.max(0, start.y + me.clientY - sy) } }));
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setPositions((p) => { persistPositions(p); return p; });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const byId = useMemo(() => Object.fromEntries(datasets.map((d) => [d.id, d])), [datasets]);
  const shown = useMemo(() => datasets.filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase())
    || (d.schema || []).some((c) => c.name.toLowerCase().includes(q.toLowerCase()))), [datasets, q]);
  const shownIds = useMemo(() => new Set(shown.map((d) => d.id)), [shown]);
  const visibleContracts = (contracts || []).filter((c) => shownIds.has(c.left_dataset_id) && shownIds.has(c.right_dataset_id));
  const keyFields = useMemo(() => {
    const m = {};
    (contracts || []).forEach((c) => (c.keys || []).forEach((k) => {
      (m[c.left_dataset_id] = m[c.left_dataset_id] || new Set()).add(k.left_field);
      (m[c.right_dataset_id] = m[c.right_dataset_id] || new Set()).add(k.right_field);
    }));
    return m;
  }, [contracts]);

  const changed = () => { load(); onContractsChanged && onContractsChanged(); };
  const toggleSet = (setter) => (id) => setter((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const heightOf = (ds) => cardHeight(ds, expanded.has(ds.id), measuresOpen.has(ds.id), models[ds.id]);
  const canvasSize = useMemo(() => {
    let w = 900, h = 500;
    shown.forEach((d) => { const p = positions[d.id]; if (p) { w = Math.max(w, p.x + CARD_W + 80); h = Math.max(h, p.y + heightOf(d) + 80); } });
    return { w, h };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, shown, expanded, measuresOpen, models]);

  const lineFor = (c) => {
    const a = positions[c.left_dataset_id], b = positions[c.right_dataset_id];
    if (!a || !b) return null;
    const ah = heightOf(byId[c.left_dataset_id]), bh = heightOf(byId[c.right_dataset_id]);
    const ac = { x: a.x + CARD_W / 2, y: a.y + ah / 2 };
    const bc = { x: b.x + CARD_W / 2, y: b.y + bh / 2 };
    const leftFirst = ac.x <= bc.x;
    const p1 = { x: leftFirst ? a.x + CARD_W : a.x, y: Math.min(ac.y, a.y + 60) };
    const p2 = { x: leftFirst ? b.x : b.x + CARD_W, y: Math.min(bc.y, b.y + 60) };
    const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    return { p1, p2, mid, leftFirst };
  };

  if (contracts === null) return <div className="flex-1 flex items-center justify-center text-xs text-[color:var(--ink-3)]"><Loader className="w-4 h-4 animate-spin mr-2" />Loading model…</div>;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      <div className="flex items-center gap-2 shrink-0 flex-wrap">
        <Button data-testid="model-canvas-add-rel" size="sm" onClick={() => setDialog({ create: true })}
          className="bg-[color:var(--brand-ocean)] text-white h-7 text-xs"><Plus className="w-3.5 h-3.5 mr-1" />New relationship</Button>
        <Button data-testid="model-canvas-autodetect" size="sm" variant="outline" onClick={() => setAutoOpen(true)} className="h-7 text-xs bg-[color:var(--surface)]"><Wand2 className="w-3.5 h-3.5 mr-1 text-[color:var(--brand-mint)]" />Auto-detect</Button>
        <Button data-testid="model-canvas-validate" size="sm" variant="outline" onClick={() => setValidateOpen(true)} className="h-7 text-xs bg-[color:var(--surface)]"><ShieldCheck className="w-3.5 h-3.5 mr-1 text-[color:var(--brand-ocean)]" />Validate model</Button>
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid="model-canvas-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find table or column…" className="h-7 w-48 pl-6 text-xs bg-[color:var(--surface)]" />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--ink-3)]"><Switch data-testid="model-canvas-show-hidden" checked={showHidden} onCheckedChange={setShowHidden} className="scale-75" /><Eye className="w-3 h-3" />Hidden fields</label>
        {contracts.some((c) => (c.broken_keys || []).length) && (
          <button data-testid="model-canvas-broken-count" onClick={() => setValidateOpen(true)}
            className="text-[11px] px-2 py-0.5 rounded-full bg-[color:rgba(220,38,38,0.08)] text-[color:var(--danger)] border border-[color:var(--danger)]/40">
            {contracts.filter((c) => (c.broken_keys || []).length).length} broken relationship(s) — schema changed
          </button>
        )}
        <span className="text-[11px] text-[color:var(--ink-3)] ml-auto hidden lg:inline">{contracts.length} relationship{contracts.length === 1 ? "" : "s"} · drag cards · click a field to rename / hide / retype</span>
      </div>
      <div data-testid="model-canvas" className="flex-1 min-h-0 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] overflow-auto relative"
        style={{ backgroundImage: "radial-gradient(circle, var(--glass-border) 1px, transparent 1px)", backgroundSize: "24px 24px" }}>
        <div className="relative" style={{ width: canvasSize.w, height: canvasSize.h }}>
          <svg className="absolute inset-0 pointer-events-none" width={canvasSize.w} height={canvasSize.h}>
            <defs>
              <marker id="rel-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="var(--brand-ocean)" /></marker>
              <marker id="rel-arrow-off" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="var(--ink-3)" /></marker>
              <marker id="rel-arrow-broken" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="var(--danger)" /></marker>
            </defs>
            {visibleContracts.map((c) => {
              const L = lineFor(c);
              if (!L) return null;
              const broken = (c.broken_keys || []).length > 0;
              const active = c.active !== false && !broken;
              const tgt = arrowTarget(c);
              const marker = broken ? "url(#rel-arrow-broken)" : active ? "url(#rel-arrow)" : "url(#rel-arrow-off)";
              // p1 is the LEFT dataset's port, p2 the RIGHT dataset's port
              const markerEnd = tgt === "right" || tgt === "both" ? marker : undefined;
              const markerStart = tgt === "left" || tgt === "both" ? marker : undefined;
              const card = CARDINALITY_OPTIONS.find((o) => o.value === (c.cardinality || "many_to_one")) || CARDINALITY_OPTIONS[2];
              return (
                <g key={c.id} data-testid="rel-line" data-direction={tgt} data-health={broken ? "broken" : active ? "ok" : "inactive"}>
                  <path d={`M ${L.p1.x} ${L.p1.y} C ${(L.p1.x + L.p2.x) / 2} ${L.p1.y}, ${(L.p1.x + L.p2.x) / 2} ${L.p2.y}, ${L.p2.x} ${L.p2.y}`}
                    fill="none" stroke={broken ? "var(--danger)" : active ? "var(--brand-ocean)" : "var(--ink-3)"} markerEnd={markerEnd} markerStart={markerStart}
                    strokeWidth={active || broken ? 2 : 1.5} strokeDasharray={active ? undefined : "5 4"} opacity={active ? 0.85 : broken ? 0.9 : 0.45} />
                  <text x={L.p1.x + (L.leftFirst ? 6 : -6)} y={L.p1.y - 6} textAnchor={L.leftFirst ? "start" : "end"} fontSize="10" fontFamily="monospace" fill="var(--ink-2)">{card.glyph[0]}</text>
                  <text x={L.p2.x + (L.leftFirst ? -6 : 6)} y={L.p2.y - 6} textAnchor={L.leftFirst ? "end" : "start"} fontSize="10" fontFamily="monospace" fill="var(--ink-2)">{card.glyph[1]}</text>
                </g>
              );
            })}
          </svg>
          {visibleContracts.map((c) => {
            const L = lineFor(c);
            if (!L) return null;
            const card = CARDINALITY_OPTIONS.find((o) => o.value === (c.cardinality || "many_to_one")) || CARDINALITY_OPTIONS[2];
            const broken = (c.broken_keys || []).length > 0;
            return (
              <button key={c.id} data-testid="rel-line-badge" data-health={broken ? "broken" : c.active === false ? "inactive" : "ok"}
                title={broken ? `${c.name} — broken: ${c.broken_keys.map((b) => b.field ? `${b.dataset || ""}.${b.field} missing` : "dataset missing").join(", ")} — click to fix` : `${c.name} — click to edit`}
                onClick={() => setDialog({ contract: c })}
                className={cn("absolute z-10 -translate-x-1/2 -translate-y-1/2 text-[10px] font-mono px-2 py-0.5 rounded-full border shadow-sm",
                  broken ? "bg-[color:var(--surface)] border-[color:var(--danger)] text-[color:var(--danger)]"
                  : c.active !== false
                    ? "bg-[color:var(--surface)] border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]"
                    : "bg-[color:var(--surface-2)] border-[color:var(--glass-border)] text-[color:var(--ink-3)]")}
                style={{ left: L.mid.x, top: L.mid.y }}>
                {card.glyph[0]} : {card.glyph[1]}{c.cross_filter_direction === "both" ? " ⇄" : ""}{broken ? " · broken" : c.active === false ? " · off" : ""}
              </button>
            );
          })}
          {shown.map((ds) => (
            <ModelCanvasCard key={ds.id} ds={ds} pos={positions[ds.id] || { x: 40, y: 40 }} keys={keyFields[ds.id] || new Set()}
              model={models[ds.id] || null} showHidden={showHidden} onDragStart={startDrag(ds.id)} onLoadModel={loadModel}
              onFieldMeta={(dsId, fm) => setModels((m) => ({ ...m, [dsId]: { ...(m[dsId] || {}), field_meta: fm } }))}
              onRename={() => onDatasetsChanged && onDatasetsChanged()}
              onAddMeasure={(d) => { const go = (m) => setMeasDlg({ ds: d, model: m, editMeasure: null }); models[d.id] ? go(models[d.id]) : api.get(`/datasets/${d.id}/model`).then(({ data }) => { setModels((m) => ({ ...m, [d.id]: data })); go(data); }); }}
              onEditMeasure={(d, m, meas) => setMeasDlg({ ds: d, model: m, editMeasure: meas })}
              onAddCalc={(d) => setCalcDlg({ ds: d, editField: null })}
              onEditCalc={(d, f) => setCalcDlg({ ds: d, editField: f })}
              expanded={expanded.has(ds.id)} onToggleExpand={() => toggleSet(setExpanded)(ds.id)}
              measuresOpen={measuresOpen.has(ds.id)} onToggleMeasures={() => toggleSet(setMeasuresOpen)(ds.id)} />
          ))}
          {!shown.length && <p className="absolute inset-0 flex items-center justify-center text-xs text-[color:var(--ink-3)]">No table or column matches “{q}”</p>}
        </div>
      </div>
      <RelationshipDialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}
        datasets={datasets} contract={dialog?.contract || null} initial={dialog?.initial || null} onSaved={() => { setDialog(null); changed(); }} />
      <ModelValidationSheet open={validateOpen} onOpenChange={setValidateOpen} contracts={contracts}
        onEditContract={(c) => { setValidateOpen(false); setDialog({ contract: c }); }} onChanged={changed} />
      <AutodetectAllDialog open={autoOpen} onOpenChange={setAutoOpen} datasets={datasets} onCreated={changed} />
      <MeasureEditorDialog open={!!measDlg} onOpenChange={(v) => !v && setMeasDlg(null)} datasetId={measDlg?.ds?.id} model={measDlg?.model} editMeasure={measDlg?.editMeasure}
        onSaved={(data) => { if (measDlg) setModels((m) => ({ ...m, [measDlg.ds.id]: data })); setMeasDlg(null); onContractsChanged && onContractsChanged(); }} />
      <CalcFieldDialog open={!!calcDlg} onOpenChange={(v) => !v && setCalcDlg(null)} dataset={calcDlg?.ds} editField={calcDlg?.editField}
        onSaved={() => { setCalcDlg(null); onDatasetsChanged && onDatasetsChanged(); }} />
    </div>
  );
}
